import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API } from '../App';
import { useT } from '../i18n';

/*
  Live clicks on the dashboard: the newest clicks from /clicks/log (the same
  data and access rules as the Clicks log page), refreshed every few seconds
  while the tab is visible. New rows slide in; a converted click shows its
  conversions (status and revenue) under the campaign name.
*/

const POLL_MS = 5000;
const LIMIT = 7;

function ago(ts, now, t) {
  const at = new Date(ts).getTime();
  if (!at) return '';
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return t('{n}s ago', { n: s });
  if (s < 3600) return t('{n} min ago', { n: Math.floor(s / 60) });
  return t('{n} h ago', { n: Math.floor(s / 3600) });
}

export default function LiveClickFeed({ campaigns = [], sources = {} }) {
  const { t } = useT();
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [now, setNow] = useState(Date.now());
  const seen = useRef(new Set());
  const fresh = useRef(new Set());

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (document.hidden) return;
      try {
        const r = await axios.get(API + '/clicks/log', { params: { limit: LIMIT } });
        if (!alive) return;
        const next = (r.data && r.data.rows) || [];
        const first = seen.current.size === 0;
        fresh.current = new Set();
        next.forEach((row) => {
          const id = row.click_id || row.id;
          if (!seen.current.has(id)) {
            if (!first) fresh.current.add(id);
            seen.current.add(id);
          }
        });
        setRows(next);
        setFailed(false);
      } catch (_e) {
        if (alive) setFailed(true);
      }
      if (alive) setNow(Date.now());
    };
    load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  const names = {};
  const srcOf = {};
  campaigns.forEach((c) => {
    names[c.id] = c.name;
    if (sources[c.traffic_source_id]) srcOf[c.id] = sources[c.traffic_source_id];
  });

  return (
    <div className="xp-panel lcf-panel">
      <div className="xp-panel-h3">
        <span className="lcf-title">
          <span className="lcf-dot" aria-hidden="true" />
          {t('Live clicks')}
        </span>
        <Link to="/clicks/log" className="lcf-link">
          {t('Open clicks log')} →
        </Link>
      </div>
      <div className="lcf-body">
        {rows === null && !failed ? (
          <div className="lcf-skel" aria-hidden="true">
            <i /><i /><i />
          </div>
        ) : failed && !rows ? (
          <p className="lcf-empty">{t('Could not load clicks')}</p>
        ) : rows.length === 0 ? (
          <div className="lcf-empty">
            <b>{t('No clicks yet')}</b>
            <span>{t('Send traffic to a tracking link and every click shows up here as it happens.')}</span>
          </div>
        ) : (
          <ul className="lcf" aria-live="polite">
            {rows.map((row) => {
              const id = row.click_id || row.id;
              const verdict = row.cloaked ? 'bad' : row.is_proxy ? 'warn' : 'ok';
              const label = row.cloaked ? row.cloak_reason || t('cloaked') : row.is_proxy ? t('proxy') : t('human');
              const device = [row.device_type, srcOf[row.campaign_id] || row.browser].filter(Boolean).join(' · ');
              const camp = names[row.campaign_id] || (row.campaign_id ? '#' + String(row.campaign_id).slice(0, 6) : '');
              const convs = Array.isArray(row.conversions) ? row.conversions : [];
              const convText = convs
                .map((c) => (Number(c.revenue) > 0 ? c.status + ' ' + (c.currency === 'USD' || !c.currency ? '$' + Number(c.revenue) : Number(c.revenue) + ' ' + c.currency) : c.status))
                .join(' · ');
              return (
                <li key={id} className={fresh.current.has(id) ? 'lcf-new' : undefined}>
                  <span className="lcf-cc">{row.country_code || '··'}</span>
                  <span className="lcf-what">
                    <b>{device || '—'}</b>
                    {camp ? <small> → {camp}</small> : null}
                    {convText ? <small className="lcf-conv">✓ {convText}</small> : null}
                  </span>
                  <span className={'lcf-v ' + verdict} title={row.cloak_reason || undefined}>{label}</span>
                  <span className="lcf-time">{ago(row.timestamp, now, t)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
